import { useEffect, useRef, useCallback } from 'react';
import { isIndexedDBHealthy } from '@/lib/firebase';
import { handleIndexedDBError } from '@/utils/indexedDBErrorHandler';
import { addBreadcrumb } from '@/utils/sentry';

interface UseIndexedDBHealthCheckOptions {
  enabled?: boolean;
  backgroundThreshold?: number;
  
  onUnhealthy?: () => void;
  
  onHealthy?: () => void;
}

export const useIndexedDBHealthCheck = (
  options: UseIndexedDBHealthCheckOptions = {}
) => {
  const {
    enabled = true,
    backgroundThreshold = 300000,
    onUnhealthy,
    onHealthy,
  } = options;

  const backgroundTimeRef = useRef<number | null>(null);
  const isCheckingRef = useRef(false);
  const onUnhealthyRef = useRef(onUnhealthy);
  const onHealthyRef = useRef(onHealthy);

  useEffect(() => {
    onUnhealthyRef.current = onUnhealthy;
    onHealthyRef.current = onHealthy;
  }, [onUnhealthy, onHealthy]);

  const abortControllerRef = useRef<AbortController | null>(null);

  const checkHealth = useCallback(async () => {
    if (isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      addBreadcrumb('indexeddb-health-check', 'Checking IndexedDB health', {
        backgroundTime: backgroundTimeRef.current
          ? Date.now() - backgroundTimeRef.current
          : 0,
      });

      const healthy = await isIndexedDBHealthy();

      // Check if component was unmounted during async operation
      if (abortController.signal.aborted) {
        return;
      }

      if (healthy) {
        console.info('IndexedDB health check: healthy');
        onHealthyRef.current?.();
      } else {
        console.warn('IndexedDB health check: unhealthy');
        addBreadcrumb('indexeddb-health-check', 'IndexedDB unhealthy detected', {
          action: 'triggering-recovery',
        });

        onUnhealthyRef.current?.();

        await handleIndexedDBError(
          new Error('IndexedDB health check failed - database appears corrupted or unavailable')
        );
      }
    } catch (error) {
      // Only log errors if component is still mounted
      if (!abortController.signal.aborted) {
        console.error('Error during IndexedDB health check:', error);
        await handleIndexedDBError(error);
      }
    } finally {
      isCheckingRef.current = false;
      abortControllerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        backgroundTimeRef.current = Date.now();
        addBreadcrumb('visibility-change', 'Page hidden', {
          timestamp: backgroundTimeRef.current,
        });
      } else {
        const timeInBackground = backgroundTimeRef.current
          ? Date.now() - backgroundTimeRef.current
          : 0;

        addBreadcrumb('visibility-change', 'Page visible', {
          timeInBackground,
          thresholdMs: backgroundThreshold,
        });

        if (timeInBackground > backgroundThreshold) {
          console.info(
            `Page was in background for ${Math.round(timeInBackground / 1000)}s, checking IndexedDB health...`
          );
          checkHealth();
        }

        backgroundTimeRef.current = null;
      }
    };

    checkHealth();

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Cleanup: abort any ongoing health check when component unmounts
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, backgroundThreshold, checkHealth]);
};
